import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconSearch, IconUser, IconFileInvoice, IconUsersGroup, IconUserPlus, IconEye, IconPencil, IconTrash, IconAlertTriangle } from '@tabler/icons-react';
import sohozJobCustomerService from '../../../services/sohozJobCustomerService';
import { notifications } from '@/lib/notifications';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useTranslation } from '../../../i18n/hooks/useTranslation';

// Prefer the Bangla name, fall back to English.
const displayName = (c) => c?.applicant_name_bn || c?.applicant_name_en || '—';

export default function CustomersTab({ onCreateInvoice }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(null); // customer pending deletion
    const [deleting, setDeleting] = useState(false);

    const handleDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            await sohozJobCustomerService.remove(confirmDelete.id);
            setCustomers((prev) => prev.filter((c) => c.id !== confirmDelete.id));
            notifications.show({ message: t('incomeExpense.customerDeleted') || 'গ্রাহক মুছে ফেলা হয়েছে', color: 'green' });
            setConfirmDelete(null);
        } catch (err) {
            notifications.show({ message: err.message || t('common.error'), color: 'red' });
        } finally {
            setDeleting(false);
        }
    };

    useEffect(() => {
        let alive = true;
        setLoading(true);
        sohozJobCustomerService.list()
            .then((res) => { if (alive) setCustomers(res.data || []); })
            .catch((err) => notifications.show({ message: err.message || t('common.error'), color: 'red' }))
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return customers;
        return customers.filter((c) =>
            displayName(c).toLowerCase().includes(q) ||
            (c.mobile_number || '').includes(q) ||
            (c.applicant_name_en || '').toLowerCase().includes(q)
        );
    }, [customers, search]);

    return (
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700/60 overflow-hidden">
            {/* Header + search */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-zinc-100 dark:border-zinc-800/60">
                <div className="flex items-center gap-2">
                    <IconUsersGroup size={18} className="text-zinc-500 dark:text-zinc-400" />
                    <h3 className="text-base font-semibold text-zinc-800 dark:text-zinc-200">
                        {t('incomeExpense.customersTab') || 'গ্রাহক'}
                    </h3>
                    {!loading && (
                        <span className="text-xs text-zinc-400 dark:text-zinc-500">({filtered.length})</span>
                    )}
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:w-64">
                        <IconSearch size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('incomeExpense.searchCustomer') || 'নাম বা মোবাইল খুঁজুন'}
                            className="w-full pl-9 pr-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate('/tools/form-auto-fillup/customers/new')}
                        className="inline-flex flex-none items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg whitespace-nowrap"
                    >
                        <IconUserPlus size={16} />
                        {t('incomeExpense.addCustomer') || 'গ্রাহক যোগ করুন'}
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] border-collapse text-sm">
                        <thead>
                            <tr className="text-left">
                                {[
                                    t('incomeExpense.customerName') || 'গ্রাহকের নাম',
                                    t('incomeExpense.customerPhone') || 'মোবাইল',
                                    t('incomeExpense.customerDistrict') || 'জেলা',
                                    '',
                                ].map((h, i) => (
                                    <th key={i} className={`py-3 px-4 first:pl-5 last:pr-5 text-[12px] font-semibold text-zinc-500 dark:text-zinc-400 border-b border-zinc-100 dark:border-zinc-800/60 ${i === 3 ? 'text-right' : ''}`}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: 5 }).map((_, r) => (
                                <tr key={r} className="border-b border-zinc-100 dark:border-zinc-800/40">
                                    <td className="py-[15px] px-4 first:pl-5">
                                        <div className="flex items-center gap-2.5">
                                            <div className="h-8 w-8 flex-none rounded-full bg-zinc-100 dark:bg-zinc-800 animate-pulse" />
                                            <div className="h-4 w-32 rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" />
                                        </div>
                                    </td>
                                    <td className="py-[15px] px-4"><div className="h-4 w-24 rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" /></td>
                                    <td className="py-[15px] px-4"><div className="h-4 w-20 rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" /></td>
                                    <td className="py-[15px] px-4 last:pr-5"><div className="ml-auto flex w-fit items-center gap-1.5"><div className="h-8 w-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 animate-pulse" /><div className="h-8 w-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 animate-pulse" /></div></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : filtered.length === 0 ? (
                <div className="py-16 flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                        <IconUser size={28} className="text-zinc-400 dark:text-zinc-600" />
                    </div>
                    <p className="text-sm text-zinc-500">{t('incomeExpense.noCustomers') || 'কোনো গ্রাহক নেই'}</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] border-collapse text-sm">
                        <thead>
                            <tr className="text-left">
                                {[
                                    t('incomeExpense.customerName') || 'গ্রাহকের নাম',
                                    t('incomeExpense.customerPhone') || 'মোবাইল',
                                    t('incomeExpense.customerDistrict') || 'জেলা',
                                    '',
                                ].map((h, i) => (
                                    <th key={i} className={`py-3 px-4 first:pl-5 last:pr-5 text-[12px] font-semibold text-zinc-500 dark:text-zinc-400 border-b border-zinc-100 dark:border-zinc-800/60 ${i === 3 ? 'text-right' : ''}`}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c) => (
                                <tr key={c.id} className="border-b border-zinc-100 dark:border-zinc-800/40 hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors group">
                                    <td className="py-[13px] px-4 first:pl-5">
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">
                                                {displayName(c).charAt(0)}
                                            </span>
                                            <div className="min-w-0">
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/income/customers/${c.id}`)}
                                                    className="block max-w-full truncate text-left text-sm font-semibold text-zinc-800 dark:text-zinc-100 hover:text-primary transition-colors"
                                                >
                                                    {displayName(c)}
                                                </button>
                                                {c.applicant_name_en && c.applicant_name_bn && (
                                                    <p className="text-[11px] text-zinc-400 dark:text-zinc-500 truncate">{c.applicant_name_en}</p>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-[13px] px-4 text-[13px] text-zinc-600 dark:text-zinc-300 tabular-nums whitespace-nowrap">{c.mobile_number || '—'}</td>
                                    <td className="py-[13px] px-4 text-[13px] text-zinc-500 dark:text-zinc-400 whitespace-nowrap">{c.home_district || c.present_district || '—'}</td>
                                    <td className="py-[13px] px-4 last:pr-5 text-right whitespace-nowrap">
                                        <div className="inline-flex items-center gap-1.5">
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/income/customers/${c.id}`)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700/60 transition-colors"
                                            >
                                                <IconEye size={14} />
                                                {t('incomeExpense.details') || 'বিস্তারিত'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => onCreateInvoice?.(c)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 border border-orange-100 dark:border-orange-800/40 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                                            >
                                                <IconFileInvoice size={14} />
                                                {t('incomeExpense.newInvoice') || 'ইনভয়েস'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/tools/form-auto-fillup/customers/${c.id}/edit`)}
                                                title={t('common.edit') || 'সম্পাদনা'}
                                                aria-label={t('common.edit') || 'সম্পাদনা'}
                                                className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-zinc-50 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-700/60 hover:text-primary transition-colors"
                                            >
                                                <IconPencil size={15} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setConfirmDelete(c)}
                                                title={t('common.delete') || 'মুছুন'}
                                                aria-label={t('common.delete') || 'মুছুন'}
                                                className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 border border-red-100 dark:border-red-800/40 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                                            >
                                                <IconTrash size={15} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Delete confirmation */}
            <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o && !deleting) setConfirmDelete(null); }}>
                <DialogContent className="max-w-sm rounded-2xl">
                    <div className="flex flex-col items-center text-center gap-3 pt-1">
                        <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                            <IconAlertTriangle size={24} className="text-red-500" />
                        </div>
                        <DialogTitle className="text-base font-semibold text-zinc-800 dark:text-zinc-100">
                            {t('incomeExpense.deleteCustomerTitle') || 'গ্রাহক মুছে ফেলবেন?'}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-zinc-500 dark:text-zinc-400">
                            <span className="font-medium text-zinc-700 dark:text-zinc-200">{confirmDelete ? displayName(confirmDelete) : ''}</span>
                            {' — '}
                            {t('incomeExpense.deleteCustomerDesc') || 'এই গ্রাহকের তথ্য স্থায়ীভাবে মুছে যাবে, এটি ফিরিয়ে আনা যাবে না।'}
                        </DialogDescription>
                        <div className="flex items-center gap-2 w-full mt-1">
                            <button
                                type="button"
                                onClick={() => setConfirmDelete(null)}
                                disabled={deleting}
                                className="flex-1 px-4 py-2 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 text-sm font-medium rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50 transition-colors"
                            >
                                {t('common.cancel') || 'বাতিল'}
                            </button>
                            <button
                                type="button"
                                onClick={handleDelete}
                                disabled={deleting}
                                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg disabled:opacity-60 transition-colors"
                            >
                                {deleting ? (t('common.deleting') || 'মুছে ফেলা হচ্ছে…') : (t('common.delete') || 'মুছুন')}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
